'use client'

import { motion, Variants } from 'framer-motion';
import { Icon } from '../ui/Icon';
import { ParagraphSection } from '@/types/drupal';

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2, delayChildren: 0.2 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] },
  },
};

const lineVariants: Variants = {
  hidden: { scaleX: 0 },
  visible: {
    scaleX: 1,
    transition: { duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] },
  },
};

const stepIcons = ['Search', 'PenTool', 'Code', 'Rocket'] as const;

export default function Process({ data }: { data: ParagraphSection }) {
  const steps = data.heroItems || [];

  return (
    <section
      id="process"
      className="Process my-[30px] lg:my-[50px] overflow-hidden"
      aria-label="Process"
    >
      <div className="lg:border-t lg:border-b lg:border-[#D9D9D9]">
        <div className="site-container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={containerVariants}
            className="py-[60px] lg:py-[110px]"
          >
            {/* Header */}
            <motion.div variants={itemVariants} className="text-center mb-16 lg:mb-24">
              {data.eyebrow && (
                <span className="text-[#0A4492] text-sm font-semibold tracking-widest uppercase mb-4 block">
                  {data.eyebrow}
                </span>
              )}
              <div
                className="Process__heading section-heading text-center"
                dangerouslySetInnerHTML={{ __html: data.title?.processed || '' }}
              />
              {data.body?.processed && (
                <div
                  className="text-[#4A5568] text-base lg:text-lg mt-6 max-w-2xl mx-auto"
                  dangerouslySetInnerHTML={{ __html: data.body.processed }}
                />
              )}
            </motion.div>

            {/* Steps */}
            <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0">
              {/* Horizontal connecting line — desktop only */}
              <motion.div
                variants={lineVariants}
                className="hidden lg:block absolute top-9 left-[12%] right-[12%] h-0.5 bg-linear-to-r from-[#002E84] via-[#0A4492] to-[#002E84] origin-left z-0"
                aria-hidden="true"
              />

              {steps.map((step, index) => {
                const iconName = stepIcons[index] || 'Circle';
                const stepNumber = step.ctaLabel || String(index + 1);

                return (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="relative flex flex-col items-center text-center px-6 lg:px-8 z-10"
                  >
                    {/* Step number badge */}
                    <div className="relative mb-6">
                      <div className="flex items-center justify-center w-18 h-18 rounded-full bg-[#002E84] shadow-[0_4px_20px_rgba(0,46,132,0.25)] transition-transform duration-300 hover:scale-105">
                        <Icon name={iconName} size={28} className="text-white" />
                      </div>
                      <span className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-[#FCFED1] text-[#002E84] text-xs font-bold flex items-center justify-center shadow-sm">
                        {stepNumber}
                      </span>
                    </div>

                    <h3 className="text-[#01060F] text-lg lg:text-xl font-semibold mb-3">
                      {step.title?.processed?.replace(/<[^>]*>/g, '') || ''}
                    </h3>

                    <div className="w-8 h-0.5 bg-[#0A4492] mb-4 rounded-full" aria-hidden="true" />

                    <p className="text-[#4A5568] text-sm lg:text-base leading-relaxed max-w-70">
                      {step.body?.processed?.replace(/<[^>]*>/g, '') || ''}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
